import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert } from 'react-bootstrap';
import { dataApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const DataManagement = () => {
  useEffect(() => {
    console.log('DataManagement component rendered');
  }, []);

  const { currentUser } = useAuth();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportMessage, setExportMessage] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [importMessage, setImportMessage] = useState(null);
  const [importError, setImportError] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveMessage, setArchiveMessage] = useState(null);
  const [archiveError, setArchiveError] = useState(null);

  const isOwner = currentUser && currentUser.role === 'owner';
  const isAdmin = currentUser && (currentUser.role === 'owner' || currentUser.role === 'admin');

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setImportMessage(null);
    setImportError(null);
  };

  const handleExport = () => {
    setExportMessage('Export initiated. Your download should start shortly...');
    setExportError(null);
    
    try {
      dataApi.exportData(
        // Success callback
        () => {
          setExportMessage('Export completed successfully. Check your downloads folder.');
        },
        // Error callback
        (err) => {
          setExportError('Failed to export data. Please try again.');
          console.error('Export error:', err);
        }
      );
    } catch (err) {
      setExportError('Failed to export data. Please try again.');
      console.error('Export error:', err);
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!file) {
      setImportError('Please select a file to import.');
      return;
    }

    setLoading(true);
    setImportMessage(null);
    setImportError(null);

    try {
      const response = await dataApi.importData(file);
      setImportMessage(`Successfully imported ${response.data.files.length} files.`);
      setFile(null);
      // Reset the file input
      document.getElementById('fileInput').value = '';
    } catch (err) {
      setImportError(err.response?.data?.detail || 'Failed to import data. Please try again.');
      console.error('Import error:', err);
    } finally {
      setLoading(false);
    }
  };

  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  const handleArchiveClick = () => {
    setShowArchiveConfirm(true);
  };

  const handleArchiveCancel = () => {
    setShowArchiveConfirm(false);
  };

  const handleArchive = async () => {
    setShowArchiveConfirm(false);
    setArchiveLoading(true);
    setArchiveMessage(null);
    setArchiveError(null);

    try {
      const response = await dataApi.archiveData();
      
      // Format the results for display
      const results = response.data.results;
      let resultMessage = 'Archive completed successfully:\n';
      
      // Format expenses results
      if (Object.keys(results.expenses).length > 0) {
        resultMessage += '\nExpenses:\n';
        for (const [year, count] of Object.entries(results.expenses)) {
          resultMessage += `- ${year}: ${count} items\n`;
        }
      } else {
        resultMessage += '\nNo expenses to archive.\n';
      }
      
      // Format payments results
      if (Object.keys(results.payments).length > 0) {
        resultMessage += '\nPayments:\n';
        for (const [year, count] of Object.entries(results.payments)) {
          resultMessage += `- ${year}: ${count} items\n`;
        }
      } else {
        resultMessage += '\nNo payments to archive.\n';
      }
      
      // Format expense payments results
      if (Object.keys(results.expense_payments).length > 0) {
        resultMessage += '\nExpense Payments:\n';
        for (const [year, count] of Object.entries(results.expense_payments)) {
          resultMessage += `- ${year}: ${count} items\n`;
        }
      } else {
        resultMessage += '\nNo expense payments to archive.\n';
      }
      
      setArchiveMessage(resultMessage);
    } catch (err) {
      setArchiveError(err.response?.data?.detail || 'Failed to archive data. Please try again.');
      console.error('Archive error:', err);
    } finally {
      setArchiveLoading(false);
    }
  };

  return (
    <Container className="mt-4">
      <h1>Data Management</h1>
      <p className="text-muted">
        Manage your data files, including import, export, and year-based archiving.
      </p>

      <Row className="mt-4">
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>Export Data</Card.Header>
            <Card.Body>
              <p>
                Export all data files as a zip archive. This includes all members, payments, expenses, and other data.
              </p>
              <Button 
                variant="primary" 
                onClick={handleExport}
                disabled={!isAdmin}
              >
                Export Data
              </Button>
              {!isAdmin && (
                <Alert variant="warning" className="mt-3">
                  Only owners and admins can export data.
                </Alert>
              )}
              {exportMessage && (
                <Alert variant="success" className="mt-3">
                  {exportMessage}
                </Alert>
              )}
              {exportError && (
                <Alert variant="danger" className="mt-3">
                  {exportError}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>Import Data</Card.Header>
            <Card.Body>
              <p>
                Import data from a zip archive. This will replace any existing data with the same IDs.
              </p>
              <Form onSubmit={handleImport}>
                <Form.Group controlId="fileInput" className="mb-3">
                  <Form.Label>Select a zip file to import</Form.Label>
                  <Form.Control 
                    type="file" 
                    onChange={handleFileChange}
                    accept=".zip"
                    disabled={!isOwner || loading}
                  />
                </Form.Group>
                <Button 
                  variant="primary" 
                  type="submit"
                  disabled={!isOwner || !file || loading}
                >
                  {loading ? 'Importing...' : 'Import Data'}
                </Button>
              </Form>
              {!isOwner && (
                <Alert variant="warning" className="mt-3">
                  Only owners can import data.
                </Alert>
              )}
              {importMessage && (
                <Alert variant="success" className="mt-3">
                  {importMessage}
                </Alert>
              )}
              {importError && (
                <Alert variant="danger" className="mt-3">
                  {importError}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card>
            <Card.Header>Archive Data by Year</Card.Header>
            <Card.Body>
              <p>
                Archive existing data into year-based files. This will improve performance by only loading the current year's data by default.
              </p>
              <p>
                <strong>Note:</strong> This operation will read the original data files and split them into year-specific files.
                After archiving, you should export the data as a backup.
              </p>
              <Button 
                variant="warning" 
                onClick={handleArchiveClick}
                disabled={!isOwner || archiveLoading}
              >
                {archiveLoading ? 'Archiving...' : 'Archive Data by Year'}
              </Button>
              {showArchiveConfirm && (
                <Alert variant="danger" className="mt-3">
                  <p><strong>Warning:</strong> This operation will reorganize your data files. It's recommended to export your data first as a backup.</p>
                  <p>Are you sure you want to proceed with archiving?</p>
                  <div>
                    <Button variant="danger" onClick={handleArchive} className="me-2">Yes, Archive Data</Button>
                    <Button variant="secondary" onClick={handleArchiveCancel}>Cancel</Button>
                  </div>
                </Alert>
              )}
              {!isOwner && (
                <Alert variant="warning" className="mt-3">
                  Only owners can archive data.
                </Alert>
              )}
              {archiveMessage && (
                <Alert variant="success" className="mt-3">
                  <pre style={{ whiteSpace: 'pre-wrap' }}>{archiveMessage}</pre>
                </Alert>
              )}
              {archiveError && (
                <Alert variant="danger" className="mt-3">
                  {archiveError}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default DataManagement;
